import { useState, useEffect } from 'react';
import styled from '@emotion/styled'
import { Link } from 'react-router-dom';
import { Popper, Paper, Divider, IconButton, Card, CardContent, CardHeader, Avatar, Tooltip, Icon } from '@mui/material';

import { BasicFlex } from '../../../utils/Common';

import { formatGenericDate, formatGenericTime, formatCurrency, formatPhoneNumber } from '../../../utils/util';

import ProfileBar from '../../../utils/utilities/ProfileBar';

import { CardBlockContainer, CardBlock, CardBlockHeader, CardSpacer } from '../../../utils/utilities/CardComponents';

import { CheckoutContext } from '../../../contexts/Checkout';
import Dialog from '../../../utils/utilities/Dialog';
import EditCheckout from '../../Checkout/EditCheckout';

import MultiStatusChip from '../../../utils/utilities/MultiStatusChip';

import SimpleChip from '../../../utils/utilities/SimpleChip';

// reusable components
import AppointmentStatus from './AppointmentStatus';
import ModifyAppointment from './ModifyAppointment';

// mui icons
import CloseIcon from '@mui/icons-material/Close';
import AutoFixHighRoundedIcon from '@mui/icons-material/AutoFixHighRounded';
import TodayRoundedIcon from '@mui/icons-material/TodayRounded';
import Person3RoundedIcon from '@mui/icons-material/Person3Rounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import EventRepeatRoundedIcon from '@mui/icons-material/EventRepeatRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';


const ApptDialog = styled.div`
  height: 100%;
`

const CustomerBlock = styled(BasicFlex)`
  gap: 1rem;
  padding: 1rem 1.6rem;
  background-color: ${props => props.theme.colors.lightBg};
`;

const CustomerInfo = styled(BasicFlex)`
  gap: .3rem;

  .title {
    font-weight: 600;
    font-size: 1.6rem;
  }
`;

const TopBar = styled(BasicFlex)`
  padding: .5rem 1rem;
  justify-content: flex-end;
  min-width: 32rem;
  margin-bottom: ${props => props.$mb ? "1.4rem" : "0"};
`;

const ViewButton = styled.div`
  flex-grow: 1;
`

const ActionBar = styled.div`
  margin-right: 1rem;
`;

const MainInfoCard = styled(Card)`
  margin-top: .5rem;
`;

const ToggleDiv = styled.div`
  height: 100%;
`

const StatusContainer = styled.div`
  width: fit-content;
  margin-bottom: 1.6rem;
`;

const TotalLine = styled.div`
  margin: .1rem 0;
  font-size: 1.2rem;

  .total {
    font-weight: 600;
  }
`


const DisplayPaymentStatus = ({invoice_status, total, balance}) => {
  if (!invoice_status) return null;
  let status_color, status;
  if (invoice_status) {
    if (invoice_status === "paid") {
      status_color = "success";
      status = "Paid";
    } else if (invoice_status === "partially_paid") {
      status_color = "warning";
      status = "Partially Paid";
    } else {
      status_color = "error";
      status = "Unpaid";
    }
  }
  return (
    <BasicFlex justify="space-between" gap="1rem">
      <Tooltip title={status}><PaidRoundedIcon color={status_color} fontSize='large' /></Tooltip>
      <BasicFlex dir="column">
        <TotalLine>Total: &nbsp;<span className='total'>{formatCurrency(total)}</span></TotalLine>
        <TotalLine>Balance: &nbsp;<span className='total'>{formatCurrency(balance)}</span></TotalLine>
      </BasicFlex>
    </BasicFlex>
  )

}


const AppointmentMenu = ({children, event = {}, ...props }) => {
  const [appointment, setAppointment] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (e) => {
    e.stopPropagation();
    setAnchorEl(anchorEl ? null : e.currentTarget);
  };

  let status_color, status;
  if (event.invoice_status) {
    if (event.invoice_status === "paid") {
      status_color = "success";
      status = "Paid";
    } else if (event.invoice_status === "partially_paid") {
      status_color = "warning";
      status = "Partially Paid";
    } else {
      status_color = "error";
      status = "Unpaid";
    }
  }

  const basicBlock = [
    {header: "", icon: <TodayRoundedIcon fontSize="small" />, info: `${formatGenericTime(event.start_time)} - ${formatGenericTime(event.end_time)}`, spread: "2rem"},
    {header: "", icon: <Person3RoundedIcon fontSize="small" />, info: <SimpleChip label={event.stylist} />, spread: "2rem"},
    {header: "", icon: <AccessTimeRoundedIcon fontSize="small" />, info: `${event.duration_in_minutes} minutes`, spread: "2rem"}
  ]

  if (event.appointment_type === "customer") {
    basicBlock.push({header: "", icon: <PaidRoundedIcon fontSize="small" />, info: formatCurrency(event.invoice_total || event.subtotal), spread: "2rem"});
  }

  if (event.appointment_type !== "customer" && event.is_recurring) {
    basicBlock.unshift({header: "", icon: <EventRepeatRoundedIcon fontSize="small" />, info: `Recurring ${event.recurrence_pattern?.frequency}`, spread: "2rem"})
  }

  return (
    <CheckoutContext.Provider value={{ appointment, setAppointment }} >
      <ApptDialog>
        <ToggleDiv onClick={handleClick}>{children}</ToggleDiv>
        <Popper open={Boolean(anchorEl)} anchorEl={anchorEl} sx={{zIndex: 1010}}>
          <Paper sx={{padding: ".5rem"}}>
            <TopBar $mb={event.appointment_type === "customer"}>
              <ViewButton>
                <IconButton LinkComponent={Link} to={`/appointments/view/${event.appointment_id}`}>
                  <SearchRoundedIcon />
                </IconButton>
              </ViewButton>
            {event.appointment_type === "customer" ? <ActionBar>
                <AppointmentStatus event={event} />
              </ActionBar> : null}
              <Divider variant='middle' orientation='vertical' flexItem />
              <IconButton onClick={handleClick} size="small">
                <CloseIcon />
              </IconButton>
            </TopBar>

          {event.appointment_type === "customer" ? (
            <> 
            <Divider />
              <ProfileBar name={event.customer} phone={event.customer_phone} event={event} />
              <Divider />
            </>) : null }

            <MainInfoCard elevation={0}>
              <CardHeader 
                title={<BasicFlex gap="1rem" justify="flex-start"><AutoFixHighRoundedIcon fontSize="small" /> {event.title}</BasicFlex>} subheader={formatGenericDate(event.appointment_date)} slotProps={{title: {sx: {fontSize: "1.6rem"}}}}
                action={<DisplayPaymentStatus invoice_status={event.invoice_status} total={event.invoice_total} balance={event.invoice_balance} />}
              />
              <CardContent sx={{paddingTop: "0"}}>
                <StatusContainer>
                  {event.appointment_type === "customer" ? (
                    <MultiStatusChip value={event.status} values={[{value: "scheduled", label: "Scheduled", status: "primary"}, {value: "in_progress", label: "In Progress", status: "warning"}, {value: "completed", label: "Show", status: "success"}, {value: "no_show", status: "error"}, {value: "cancelled", label: "Cancelled", status: "error"}]} />
                  ) : (
                    <MultiStatusChip value={event.appointment_type} values={[{value: "personal_block", label: "Personal Event", status: "secondary"}, {value: "block", label: "Public Event", status: "primary"}]} />
                  )
                  }
                </StatusContainer>

                <CardBlockContainer>
                  {/* <CardBlockHeader>Summary</CardBlockHeader> */}
                  {basicBlock.map((block, i) => (
                    <CardBlock key={i} {...block} />
                  ))}
                </CardBlockContainer>
              </CardContent>

              <ModifyAppointment event={event} setAnchorEl={setAnchorEl} />
            </MainInfoCard>
          </Paper>
        </Popper>
        <Dialog fullScreen forceOpen={appointment?.appointment_id ? true : false} title="Appointment Checkout" useForm={true} forceClose={() => setAppointment(null)}>
          <EditCheckout />
        </Dialog>
      </ApptDialog>
    </CheckoutContext.Provider>
  );
};

export default AppointmentMenu;